import { useState, useCallback } from "react";
import { CloudUpload, X, LoaderCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { todayIST } from "@emr/shared/india";
import { Progress } from "@/components/ui/progress";
import { usePresignDocument, useFinalizeDocument } from "@/lib/mutations";
import { convertToPdf } from "@/lib/pdf/convertToPdf";
import { toast } from "sonner";

interface Props {
  patientId?: string;
  visitId?: string;
  /** Called after at least one document uploads successfully. */
  onUploaded?: () => void;
}

interface PendingFile {
  /** Stable identity for list keys / targeted updates. */
  uid: string;
  /** Converted PDF payload (set once conversion finishes). */
  blob?: Blob;
  originalName: string;
  fileName: string;
  sizeLabel: string;
  sourceType: "pdf" | "image";
  category: string;
  description: string;
  documentDate: string;
  status: "converting" | "pending" | "uploading" | "finalizing" | "done" | "error";
  progress: number;
  error?: string;
}

const today = () => todayIST();

function fmtSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Drag-drop upload box for patient/visit documents — used inline inside the
 * DocumentViewer dialog and the patient Documents tab.
 *
 * Upload policy: only PDF and image files are accepted. Images are converted
 * client-side to a compressed (JPEG q0.7, Flate) 72-DPI A4 PDF before upload,
 * so every user-uploaded document is stored as a PDF. See lib/pdf/convertToPdf.ts.
 */
export function UploadFiles({ patientId, visitId, onUploaded }: Props) {
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const presignMut = usePresignDocument();
  const finalizeMut = useFinalizeDocument();

  // Patch by uid, NOT by object reference: the first patch replaces the array
  // element with a NEW object, so any caller still holding the pre-patch
  // reference (e.g. uploadAll's catch after uploadOne's first patch) would
  // silently no-op with f === pf — the exact bug that left failed uploads
  // spinning forever with no error shown (2026-10-17 postmortem).
  const patch = (pf: PendingFile, changes: Partial<PendingFile>) => {
    setFiles((prev) => prev.map((f) => (f.uid === pf.uid ? { ...f, ...changes } : f)));
  };

  // Stage 1: accept file → convert images to a 72-DPI compressed PDF.
  const addFiles = useCallback((fileList: FileList | File[]) => {
    for (const file of Array.from(fileList)) {
      const slot: PendingFile = {
        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        originalName: file.name,
        fileName: file.name,
        sizeLabel: fmtSize(file.size),
        sourceType: /\.pdf$/i.test(file.name) || file.type === "application/pdf" ? "pdf" : "image",
        category: "General",
        description: "",
        documentDate: today(),
        status: "converting",
        progress: 5,
      };
      setFiles((prev) => [...prev, slot]);
      convertToPdf(file)
        .then((converted) => {
          patch(slot, {
            blob: converted.blob,
            fileName: converted.fileName,
            sizeLabel: fmtSize(converted.sizeBytes),
            sourceType: converted.sourceType,
            status: "pending",
            progress: 0,
          });
        })
        .catch((e: Error) => {
          patch(slot, { status: "error", error: e.message });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  }, [addFiles]);

  const remove = (pf: PendingFile) => {
    setFiles((prev) => prev.filter((f) => f !== pf));
  };

  const updateMeta = (pf: PendingFile, field: "category" | "description" | "documentDate", value: string) => {
    patch(pf, { [field]: value });
  };

  // Stage 2: presign → PUT to R2 → finalize (insert DB row).
  const uploadOne = async (pf: PendingFile) => {
    if (!pf.blob) throw new Error("PDF not ready");
    patch(pf, { status: "uploading", progress: 10, error: undefined });
    const presigned = await presignMut.mutateAsync({
      fileName: pf.fileName,
      mimeType: "application/pdf",
      sizeBytes: pf.blob.size,
      patientId,
      visitId,
      type: pf.category,
    });

    patch(pf, { progress: 40 });
    // AbortSignal.timeout: a hung R2 PUT (network stall, captive portal) must
    // surface as an error instead of spinning forever (2026-10-17 postmortem).
    let putRes: Response;
    try {
      putRes = await fetch(presigned.presignedUrl, {
        method: "PUT",
        body: pf.blob,
        headers: { "Content-Type": "application/pdf" },
        signal: AbortSignal.timeout(180_000),
      });
    } catch (e) {
      // A CORS-blocked or network-dead PUT throws TypeError "Failed to fetch"
      // — cryptic for clinic staff. Surface the likely cause instead.
      const msg = (e as Error).name === "TimeoutError"
        ? "Upload timed out — check your internet connection and retry"
        : "Upload failed before reaching storage (network/CORS) — check your connection and retry";
      throw new Error(msg, { cause: e });
    }
    if (!putRes.ok) throw new Error(`R2 PUT failed: ${putRes.status}`);

    patch(pf, { status: "finalizing", progress: 80 });
    await finalizeMut.mutateAsync({
      patientId: patientId ?? null,
      visitId: visitId ?? null,
      type: pf.category,
      fileName: pf.fileName,
      r2Key: presigned.r2Key,
      mimeType: "application/pdf",
      sizeBytes: pf.blob.size,
      description: pf.description || null,
      documentDate: pf.documentDate,
    });

    patch(pf, { status: "done", progress: 100 });
  };

  const uploadAll = async () => {
    const targets = files.filter((f) => f.status === "pending" && f.blob);
    if (targets.length === 0) return;
    // Bounded concurrency 3 (2026-10-31, Batch 4): hand-rolled chunking, no
    // new deps. N serial uploads made a big batch take N × full-pipeline
    // time; 3 concurrent keeps per-file patch()/progress updates flowing
    // while capping parallel PUTs (R2 + Worker friendly). Per-file try/catch
    // + patch-by-uid status/error isolation are preserved exactly — a failed
    // chunk marks only its own files as error and never aborts the rest.
    const CONCURRENCY = 3;
    let ok = 0;
    for (let i = 0; i < targets.length; i += CONCURRENCY) {
      const chunk = targets.slice(i, i + CONCURRENCY);
      const results = await Promise.allSettled(chunk.map((pf) => uploadOne(pf)));
      for (let j = 0; j < results.length; j++) {
        const r = results[j];
        if (r.status === "fulfilled") {
          ok += 1;
        } else {
          // uploadOne throws BEFORE its terminal patch (done / error status):
          // mark the failed file here from the caught reason. patch-by-uid
          // is closure-safe (see the 2026-10-17 postmortem note above).
          patch(chunk[j], { status: "error", error: (r.reason as Error)?.message ?? "Upload failed" });
        }
      }
    }
    if (ok > 0) {
      toast.success(`Uploaded ${ok} document${ok > 1 ? "s" : ""}`);
      onUploaded?.();
    }
  };

  const clearFinished = () => {
    setFiles((prev) => prev.filter((f) => f.status !== "done" && f.status !== "error"));
  };

  return (
    <div className="space-y-2">
      <div
        className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${dragOver ? "border-maroon-400 bg-cream-50" : "border-cream-200 dark:border-dark-700"}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <CloudUpload size={24} className="mx-auto mb-1 text-muted" />
        <div className="text-sm text-muted">Drag files here, or</div>
        <label className="mt-1 inline-block">
          <input
            type="file"
            multiple
            accept="application/pdf,image/*"
            className="hidden"
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }}
          />
          <Button size="sm" variant="outline" asChild>
            <span>Browse files</span>
          </Button>
        </label>
        <div className="mt-1 text-xs text-muted">
          PDF or image files only — images are converted to compressed 72&nbsp;DPI PDF automatically.
        </div>
      </div>

      {files.length > 0 && (
        <div className="max-h-56 space-y-2 overflow-y-auto">
          {files.map((pf) => (
            <div key={pf.uid} className="rounded-lg border border-cream-200 p-3 dark:border-dark-700">
              <div className="mb-2 flex items-start gap-2">
                <FileText size={16} className="mt-0.5 shrink-0 text-maroon-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink dark:text-cream-50">{pf.fileName}</div>
                  <div className="text-xs text-muted">
                    {pf.sizeLabel}
                    {pf.sourceType === "image" && " · will be converted to PDF"}
                  </div>
                </div>
                {(pf.status === "pending" || pf.status === "error") && (
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => remove(pf)}>
                    <X size={14} />
                  </Button>
                )}
                {pf.status === "done" && <span className="text-xs text-green-700">Done</span>}
                {pf.status === "error" && <span className="text-xs text-red-600">Failed</span>}
              </div>

              {pf.status === "pending" && (
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-xs">Category</Label>
                    <Input value={pf.category} onChange={(e) => updateMeta(pf, "category", e.target.value)} className="h-8 text-xs" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Description</Label>
                    <Input value={pf.description} onChange={(e) => updateMeta(pf, "description", e.target.value)} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Document date</Label>
                    <Input type="date" value={pf.documentDate} onChange={(e) => updateMeta(pf, "documentDate", e.target.value)} className="h-8 text-xs" />
                  </div>
                </div>
              )}

              {(pf.status === "converting" || pf.status === "uploading" || pf.status === "finalizing") && (
                <div className="mt-2 flex items-center gap-2">
                  <Progress value={pf.progress} className="h-2" />
                  <LoaderCircle size={12} className="shrink-0 animate-spin text-muted" />
                </div>
              )}

              {pf.error && <div className="mt-1 text-xs text-red-600">{pf.error}</div>}
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={clearFinished}>Clear finished</Button>
          {files.some((f) => f.status === "pending" && f.blob) && (
            <Button size="sm" onClick={uploadAll} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              <CloudUpload size={14} className="mr-1" /> Upload {files.filter((f) => f.status === "pending" && f.blob).length} file(s)
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
